import i18next from 'i18next';
import colors from 'assets/styles/colors';

import type { TAcceptanceStatusValue, TEngagementAttemptsValue } from './types';

export const engagementAttemptsLabels: Record<
  TEngagementAttemptsValue,
  string
> = {
  overdue: i18next.t('patientEngagementReport.overdue'),
  engagement_sms_completed: i18next.t(
    'patientEngagementReport.engagementSmsCompleted',
  ),
  referral_withdrawn_by_provider: i18next.t(
    'patientEngagementReport.referralWithdrawnByProvider',
  ),
  vip_referral: i18next.t('patientEngagementReport.vipReferral'),
  appt_for_today: i18next.t('patientEngagementReport.apptForToday'),
  ready_for_engagement: i18next.t('patientEngagementReport.readyForEngagement'),
  future_appt: i18next.t('patientEngagementReport.futureAppt'),
  '4th_plus_attempt_completed': i18next.t(
    'patientEngagementReport.fourthPlusAttemptCompleted',
  ),
  '1st_attempt_completed': i18next.t(
    'patientEngagementReport.firstAttemptCompleted',
  ),
  '2nd_attempt_completed': i18next.t(
    'patientEngagementReport.secondAttemptCompleted',
  ),
  '3rd_attempt_completed': i18next.t(
    'patientEngagementReport.thirdAttemptCompleted',
  ),
  no_contact_5th_attempt_completed: i18next.t(
    'patientEngagementReport.noContactFifthAttemptCompleted',
  ),
  no_contact_6th_attempt_completed: i18next.t(
    'patientEngagementReport.noContactSixthAttemptCompleted',
  ),
  no_contact_4th_attempt_completed: i18next.t(
    'patientEngagementReport.noContactFourthAttemptCompleted',
  ),
};

export const acceptanceStatusLabels: Record<TAcceptanceStatusValue, string> = {
  do_not_send_sms: i18next.t('patientEngagementReport.doNotSendSms'),
  onboarding: i18next.t('patientEngagementReport.onboarding'),
  referral_withdrawn: i18next.t('patientEngagementReport.referralWithdrawn'),
  patient_notified: i18next.t('patientEngagementReport.patientNotified'),
  patient_refused_copay_insurance: i18next.t(
    'patientEngagementReport.patientRefusedCopayInsurance',
  ),
  pending: i18next.t('patientEngagementReport.pending'),
  patient_refused_connectivity_tech_barrier: i18next.t(
    'patientEngagementReport.patientRefusedConnectivityTechBarrier',
  ),
  patient_refused_not_interested: i18next.t(
    'patientEngagementReport.patientRefusedNotInterested',
  ),
  patient_refused_deceased: i18next.t(
    'patientEngagementReport.patientRefusedDeceased',
  ),
  patient_refused_no_one_to_help: i18next.t(
    'patientEngagementReport.patientRefusedNoOneToHelp',
  ),
  no_contact: i18next.t('patientEngagementReport.noContact'),
  patient_refused_wants_to_discuss_with_physician: i18next.t(
    'patientEngagementReport.patientRefusedWantsToDiscussWithPhysician',
  ),
  wrong_contact_information: i18next.t(
    'patientEngagementReport.wrongContactInformation',
  ),
  patient_no_contact: i18next.t('patientEngagementReport.patientNoContact'),
  patient_refused_hospice_snf: i18next.t(
    'patientEngagementReport.patientRefusedHospiceSnf',
  ),
  pt_refused_seen_clinic_once: i18next.t(
    'patientEngagementReport.ptRefusedSeenClinicOnce',
  ),
  patient_refused_rpm_different_provider: i18next.t(
    'patientEngagementReport.patientRefusedRpmDifferentProvider',
  ),
  pt_refused_could_not_verify_identity: i18next.t(
    'patientEngagementReport.ptRefusedCouldNotVerifyIdentity',
  ),
  patient_refused_did_not_allow_explanation: i18next.t(
    'patientEngagementReport.patientRefusedDidNotAllowExplanation',
  ),
  patient_refused_suspect_scam: i18next.t(
    'patientEngagementReport.patientRefusedSuspectScam',
  ),
  patient_refused_no_longer_attending_clinic: i18next.t(
    'patientEngagementReport.patientRefusedNoLongerAttendingClinic',
  ),
  phone_number_out_of_service: i18next.t(
    'patientEngagementReport.phoneNumberOutOfService',
  ),
  patient_refused_requested_more_information: i18next.t(
    'patientEngagementReport.patientRefusedRequestedMoreInformation',
  ),
  patient_refused_does_not_recognize_physician_facility: i18next.t(
    'patientEngagementReport.patientRefusedDoesNotRecognizePhysicianFacility',
  ),
  patient_refused_declined_by_caregiver: i18next.t(
    'patientEngagementReport.patientRefusedDeclinedByCaregiver',
  ),
  patient_refused_no_contact_call_blocker: i18next.t(
    'patientEngagementReport.patientRefusedNoContactCallBlocker',
  ),
  appointment_scheduled: i18next.t(
    'patientEngagementReport.appointmentScheduled',
  ),
  onboarding_rpm_only: i18next.t('patientEngagementReport.onboardingRpmOnly'),
  onboarding_ccm_only: i18next.t('patientEngagementReport.onboardingCcmOnly'),
  onboarding_rpm_and_ccm: i18next.t(
    'patientEngagementReport.onboardingRpmAndCcm',
  ),
  refused: i18next.t('patientEngagementReport.refused'),
};

/**
 * Slice order for the Engagement Attempts Overview donut - it drives both the
 * ring's fill order and the legend's reading order. The four "… Attempt
 * Completed" states (indexes 9/10/11/7) and the three "No Contact - Nth attempt"
 * states (14/12/13) are kept as contiguous, in-sequence blocks so their shared
 * green/red ramps read as an ordinal progression; the remaining workflow states
 * are interleaved so that no two distinct-category slices that a red-green
 * colourblind reader could confuse ever sit next to each other (see
 * `engagementAttemptsColors`). Every value is present; the hook drops the
 * zero-count ones, so in practice only the populated options render.
 */
export const engagementAttemptsValues: TEngagementAttemptsValue[] = [
  'future_appt',
  'appt_for_today',
  'referral_withdrawn_by_provider',
  'overdue',
  'engagement_sms_completed',
  'ready_for_engagement',
  'vip_referral',
  '1st_attempt_completed',
  '2nd_attempt_completed',
  '3rd_attempt_completed',
  '4th_plus_attempt_completed',
  'no_contact_4th_attempt_completed',
  'no_contact_5th_attempt_completed',
  'no_contact_6th_attempt_completed',
];

/**
 * The Engagement Attempts Overview donut/legend is restricted to these engagement
 * options only; every other Monday option is dropped from the chart entirely.
 * Order is irrelevant here - slice/legend order still comes from
 * `engagementAttemptsValues`. The `attempt_completed` group row appears
 * automatically because 1st/2nd/3rd carry its `groupId`.
 */
export const engagementAttemptsVisibleValues =
  new Set<TEngagementAttemptsValue>([
    'future_appt',
    'appt_for_today',
    'ready_for_engagement',
    '1st_attempt_completed',
    '2nd_attempt_completed',
    '3rd_attempt_completed',
  ]);

/**
 * Contiguous "… Attempt Completed" runs on the Engagement Attempts Overview donut
 * are collapsed into a single expandable legend row. Each group carries its own
 * (distinct) colour so the summary row reads as a roll-up, not one of the ramp
 * steps; expanding it reveals the individual states unchanged.
 */
export const engagementAttemptsGroups: Array<{
  id: string;
  label: string;
  values: TEngagementAttemptsValue[];
  color: string;
}> = [
  {
    id: 'attempt_completed',
    label: i18next.t('patientEngagementReport.attemptCompleted'),
    values: [
      '1st_attempt_completed',
      '2nd_attempt_completed',
      '3rd_attempt_completed',
      '4th_plus_attempt_completed',
    ],
    color: colors.dullGreen,
  },
  {
    id: 'no_contact_attempt_completed',
    label: i18next.t('patientEngagementReport.noContactAttemptCompleted'),
    values: [
      'no_contact_4th_attempt_completed',
      'no_contact_5th_attempt_completed',
      'no_contact_6th_attempt_completed',
    ],
    color: colors.red50,
  },
];

/**
 * Composite palette: the completed-attempt states share a light→dark GREEN ramp
 * and the No-Contact states a light→dark RED ramp (both ordinal - the closeness
 * of adjacent steps is the point, not a defect), while the workflow states take
 * distinct accent hues. 14 slices exceed what any palette can make fully
 * CVD-distinct, so the always-present labelled legend (name + count + %) is the
 * primary identity channel and colour is secondary. Validated with the dataviz
 * palette checker in light and dark: the only sub-threshold adjacencies are the
 * consecutive steps of the ordinal ramps.
 */
export const engagementAttemptsColors: Record<
  TEngagementAttemptsValue,
  string
> = {
  future_appt: '#14b8a6',
  appt_for_today: '#eab308',
  referral_withdrawn_by_provider: colors.darkGray,
  overdue: colors.orange50,
  engagement_sms_completed: colors.blue50,
  ready_for_engagement: '#ef4e9c',
  vip_referral: '#7c3aed',
  '1st_attempt_completed': colors.green10,
  '2nd_attempt_completed': colors.green30,
  '3rd_attempt_completed': colors.green50,
  '4th_plus_attempt_completed': colors.green70,
  no_contact_4th_attempt_completed: colors.red20,
  no_contact_5th_attempt_completed: colors.red40,
  no_contact_6th_attempt_completed: colors.red60,
};

/**
 * The 6 categories shown in the Status Overview donut chart. The donut is now
 * grouped by the patient's current Monday acceptance status, rolled up on the
 * backend (see `acceptanceDonutIndexesByCategory`); `pending` also absorbs the
 * no-acceptance-yet patients.
 */
export type TStatusOverviewCategory =
  | 'onboarding'
  | 'patient_refused'
  | 'no_contact'
  | 'referral_withdrawn'
  | 'appointment_scheduled'
  | 'pending';

export const statusBreakdownStatuses: TStatusOverviewCategory[] = [
  'onboarding',
  'patient_refused',
  'no_contact',
  'referral_withdrawn',
  'appointment_scheduled',
  'pending',
];

export const statusBreakdownColors: Record<TStatusOverviewCategory, string> = {
  onboarding: colors.orange50,
  patient_refused: '#EF4E9C',
  no_contact: colors.blue50,
  referral_withdrawn: colors.purple50,
  appointment_scheduled: colors.green50,
  pending: colors.yellow50,
};

export const statusBreakdownLabels: Record<TStatusOverviewCategory, string> = {
  onboarding: i18next.t('patientEngagementReport.onboarding'),
  patient_refused: i18next.t('patientEngagementReport.patientRefused'),
  no_contact: i18next.t('patientEngagementReport.noContact'),
  referral_withdrawn: i18next.t('patientEngagementReport.referralWithdrawn'),
  appointment_scheduled: i18next.t(
    'patientEngagementReport.appointmentScheduled',
  ),
  pending: i18next.t('patientEngagementReport.pending'),
};
